import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import UserBadge from '@/features/user-profile/components/UserBadge.vue';
import TextRecommendationCardModal from './TextRecommendationCardModal.vue';
import TextRecommendationModal from './TextRecommendationModal.vue';
import Popover from '@/ui-components/Popover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { useMutation } from '@/helpers/composables/useApollo';
import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';
import { isMobile } from '@/helpers/ui-helper';
import {
	LikeTextRecommendationDocument,
	LikeTextRecommendationMutation,
	LikeTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/LikeTextRecommendation.mutation';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { AccountModes } from '@/enums/login';

import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { ShareHelper } from '@/helpers/share-helper';
import { ShareMethod } from '@/enums/share-methods';
import { useRoute } from 'vue-router/composables';
import { formatDateTime } from '@/helpers/date-helper';
import { getTextRecAnchor } from '@/helpers/composables/useTextRecommendation';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationCard',
  props: {
    textRecommendation: null,
    title: null,
    isSingleTextRecommendation: { type: Boolean },
    additionalContexts: null
  },
  setup(__props: any) {

const props = __props as {
	textRecommendation: TextRecommendationFragment;
	title: TitleDetail;
	isSingleTextRecommendation?: boolean;
	additionalContexts: SnowplowContext[];
};



const { t } = useI18n();

const { language, country } = useLanguageStore();

const displayCardModal = ref(false);
const displayTextRecommendationModal = ref<boolean>(false);
const showOriginalText = ref<boolean>(false);

const liked = computed(() => props.textRecommendation.likedByUser);
const likeCount = computed(() => props.textRecommendation.likeCount);

const profile = computed(() => props.textRecommendation.profile);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);
const body = computed(() => props.textRecommendation.body);
const originalBody = computed(() => props.textRecommendation.originalBody);

const displayedHeadline = computed(() => {
	if (showOriginalText.value && originalHeadline.value) return originalHeadline.value;
	return headline.value;
});

const displayedBody = computed(() => {
	if (showOriginalText.value && originalBody.value) return originalBody.value;
	return body.value;
});

const date = computed(() => {
	const date = new Date(props.textRecommendation.watchedAt);

	return formatDateTime(date, language.value, country.value, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
});

const backgroundImageUrl = computed(() => JW_CONFIG.IMAGESCALER_URL + props.title.content.fullPosterUrl);

const watchedOn = computed(() => props.textRecommendation.watchedOn);

const watchedInCinema = computed(() => {
	return props.textRecommendation.watchedOn?.packageId === 0;
});

const tagsString = computed(() => {
	const tags = props.textRecommendation.tags;
	if (!tags || tags.length === 0) return '';

	return tags.map(tag => `#${tag.translatedName}`).join(' ');
});

const watchedOnText = computed(() => {
	if (watchedInCinema.value) return t('WEBAPP_WATCHED_CINEMA', { date: date.value });

	return t('WEBAPP_WATCHED_PROVIDER', { provider: props.textRecommendation.watchedOn?.clearName, date: date.value });
});

const formData = computed(() => {
	return {
		__typename: 'TextRecommendation' as const,
		id: props.textRecommendation.id,
		headline: headline.value,
		body: body.value,
		tags: props.textRecommendation.tags,
		watchedOn: watchedOn.value,
		watchedAt: props.textRecommendation.watchedAt,
		profile: props.textRecommendation.profile,
		ownedByUser: props.textRecommendation.ownedByUser,
		likeCount: props.textRecommendation.likeCount,
		likedByUser: props.textRecommendation.likedByUser,
	};
});

const hasTags = computed(() => props.textRecommendation.tags && props.textRecommendation.tags?.length > 0);

const isMobileViewport = computed(() => process.client && isMobile());

const displayReadMore = computed(() => {
	if (props.isSingleTextRecommendation) return false;

	const bodyLength = body.value?.length ?? 0;

	if (hasTags.value) {
		return !!((isMobileViewport.value && bodyLength > 410) || bodyLength > 505);
	} else {
		return !!((isMobileViewport.value && bodyLength > 500) || bodyLength > 560);
	}
});

const showPopover = computed(() => props.textRecommendation.ownedByUser);

const isTranslated = computed(() => {
	return !!(props.textRecommendation.originalHeadline || props.textRecommendation.originalBody);
});

const likeRecommendation = useMutation<LikeTextRecommendationMutation, LikeTextRecommendationMutationVariables>(
	LikeTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function toggleTextRecommendationModal(value: boolean) {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	displayTextRecommendationModal.value = value;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

function handleProfileCLick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'author_clicked',
			useLastRoute: true,
		},
		props.additionalContexts
	);
}

function handleLikeClick() {
	const likeValue = !liked.value;

	likeRecommendation.mutate({
		input: {
			uuid: props.textRecommendation.id,
			like: likeValue,
		},
		country: country.value,
		language: language.value,
	});

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'helpful_clicked',
			value: likeValue ? 1 : 0,
		},
		props.additionalContexts
	);
}

function handleShowTranslation() {
	showOriginalText.value = !showOriginalText.value;
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}

deleteRecommendation.onDone(() => {
	toastSuccess({
		message: 'Recommendation deleted',
		duration: 3000,
	});
});

deleteRecommendation.onError(() => {
	toastError({
		message: 'Failed to delete recommendation',
		duration: 3000,
	});
});

function toggleCardModal(display: boolean) {
	displayCardModal.value = display;

	if (displayCardModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'read_more_clicked',
			},
			props.additionalContexts
		);
	}

	displayCardModal.value ? disableScroll() : enableScroll();
}

/* SHARING */
const shareIcon = `/${ASSETS_DIR}/img/share.svg`;

const cardId = computed(() => getTextRecAnchor(() => props.textRecommendation.profile));

function shareRecommendation(method?: ShareMethod) {
	ShareHelper.share({
		title: props.title,
		tracking: {
			action: 'wtw_click',
			label: method,
			property: props.textRecommendation.id,
		},
		content: {
			url: `${JW_CONFIG.DOMAIN}${props.title.content.fullPath}#${cardId.value}`,
			text: `"${props.textRecommendation.body}" – ${props.textRecommendation.profile.displayName}`,
			method,
		},
		useNative: false,
	});
}

const route = useRoute();
const fromRecShare = ref(false);

onMounted(() => {
	if (route.hash !== `#${cardId.value}`) return;

	// Waiting a tiny bit because anchor links work right away but the page content
	// is still rendering and shifting (on mounted renders like ads, experiments, etc.)
	// This helps improve the experience.
	setTimeout(() => {
		// TextRecommendations.vue has both mobile & desktop versions of the card in the HTML
		// They're hidden with CSS and can't be selected so I select them all and check visibility
		const elements = [...document.querySelectorAll(route.hash)];
		const element = elements.find(e => e != null && e.checkVisibility());
		if (element != null) {
			element?.scrollIntoView();
			fromRecShare.value = true;
		}
	}, 250);
});

return { __sfc: true,props, t, language, country, displayCardModal, displayTextRecommendationModal, showOriginalText, liked, likeCount, profile, headline, originalHeadline, body, originalBody, displayedHeadline, displayedBody, date, backgroundImageUrl, watchedOn, watchedInCinema, tagsString, watchedOnText, formData, hasTags, isMobileViewport, displayReadMore, showPopover, isTranslated, likeRecommendation, toggleTextRecommendationModal, handleProfileCLick, handleLikeClick, handleShowTranslation, deleteRecommendation, deleteTextRecommendation, toggleCardModal, shareIcon, cardId, shareRecommendation, route, fromRecShare, UserBadge, TextRecommendationCardModal, TextRecommendationModal, Popover, FontAwesomeIcon, ShareMethod }
}

})